import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import { getPlaylistCoverUrl } from "@/lib/mediaUrls";

/**
 * ShareToCommunityDialog — lets a signed-in user share a track, beat,
 * RGM report OR a live beam into a destination (crew / group / club)
 * they have permission to post in.
 *
 * Props (any one of submissionId / beatId / reportId / liveShare is required):
 *   - open: boolean
 *   - onClose: () => void
 *   - submissionId: track id to share
 *   - trackTitle: optional title (for preview)
 *   - beatId: beat id to share (Phase 19.0)
 *   - beatTitle: optional beat title (Phase 19.0)
 *   - reportId: RGM Report id to share (Phase 19.0)
 *   - reportTitle: optional report title (Phase 19.0)
 *   - liveShare: { roomId, title, url, cover, hostName } — Phase 21.4 Batch 5
 *     Live beam share. When provided, the crew/club post uses `content`
 *     (title + URL) and `image_url` (cover) instead of shared_track_id.
 */
const ShareToCommunityDialog = ({
  open,
  onClose,
  submissionId,
  trackTitle,
  beatId,
  beatTitle,
  reportId,
  reportTitle,
  liveShare,
}) => {
  const [destinations, setDestinations] = useState({ crews: [], groups: [], clubs: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { kind, id, name }
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/users/me/post-destinations")
      .then((r) => { if (!cancelled) setDestinations(r.data || { crews: [], groups: [], clubs: [] }); })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Sign in to share into communities");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setNote("");
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!selected) {
      toast.error("Pick a destination first");
      return;
    }
    setPosting(true);
    try {
      const previewTitle = trackTitle || beatTitle || reportTitle || liveShare?.title || "this post";
      // Phase 21.4 Batch 5 — Live beam share reuses the existing
      // crew/club post surface. Composes content + image_url (cover) so
      // no new backend field is needed.
      if (liveShare?.roomId && liveShare?.url) {
        const composed = [
          `🎙 Live now: ${liveShare.title || previewTitle}`,
          liveShare.hostName ? `Host: ${liveShare.hostName}` : null,
          `Watch: ${liveShare.url}`,
          note.trim() || null,
        ].filter(Boolean).join("\n");
        if (selected.kind === "crew" || selected.kind === "group") {
          await api.post(`/crews/${selected.id}/posts`, {
            content: composed,
            image_url: liveShare.cover || null,
          });
        } else if (selected.kind === "club") {
          await api.post(`/clubs/${selected.id}/posts`, {
            title: `🎙 Live: ${liveShare.title || previewTitle}`,
            body: composed,
          });
        }
      } else if (selected.kind === "crew" || selected.kind === "group") {
        await api.post(`/crews/${selected.id}/posts`, {
          content: note.trim() || `Check out ${previewTitle}!`,
          shared_track_id: submissionId || null,
          shared_beat_id: beatId || null,
          shared_report_id: reportId || null,
        });
      } else if (selected.kind === "club") {
        await api.post(`/clubs/${selected.id}/posts`, {
          title: trackTitle ? `🎵 ${trackTitle}` : (beatTitle ? `🎹 ${beatTitle}` : (reportTitle || "New drop")),
          body: note.trim() || "Subscribers only — fresh drop for you.",
          shared_track_id: submissionId || null,
        });
      }
      toast.success(`Shared to ${selected.name}`);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not share");
    } finally {
      setPosting(false);
    }
  };

  const renderRow = (item, kind) => {
    const id = item.crew_id || item.club_id;
    const isSelected = selected?.id === id && selected?.kind === kind;
    const cover = getPlaylistCoverUrl({
      cover_image: kind === "club" ? (item.avatar_image || item.cover_image) : item.cover_image,
      cover_updated_at: kind === "club" ? (item.avatar_updated_at || item.cover_updated_at) : item.cover_updated_at,
    });
    const subLabel = kind === "club"
      ? `⭐ ${item.subscriber_count || 0} subscriber${(item.subscriber_count || 0) === 1 ? "" : "s"}`
      : `👥 ${(item.member_ids || []).length} member${(item.member_ids || []).length === 1 ? "" : "s"}`;
    return (
      <button
        key={id}
        type="button"
        onClick={() => setSelected({ kind, id, name: item.name })}
        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
          isSelected
            ? (kind === "club"
                ? "bg-amber-500/20 border-2 border-amber-500/60"
                : "bg-purple-500/20 border-2 border-purple-500/60")
            : "bg-gray-900/40 hover:bg-gray-900/80 border-2 border-transparent"
        }`}
        data-testid={`share-dest-${kind}-${id}`}
      >
        <img
          src={cover}
          alt={item.name}
          className={`w-10 h-10 ${kind === "club" ? "rounded-full" : "rounded-md"} object-cover flex-shrink-0`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-white font-medium truncate">{item.name}</p>
          <p className="text-xs text-gray-400">
            {subLabel}
            {item.visibility === "private" && " · 🔒 Private"}
          </p>
        </div>
        {isSelected && (
          <span className={kind === "club" ? "text-amber-300" : "text-purple-300"}>✓</span>
        )}
      </button>
    );
  };

  const totalDestinations =
    (destinations.crews?.length || 0) +
    (destinations.groups?.length || 0) +
    (destinations.clubs?.length || 0);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      data-testid="share-to-community-dialog"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        data-testid="share-dialog-backdrop"
      />
      <Card
        className="relative w-full sm:w-[540px] max-h-[85vh] sm:max-h-[80vh] bg-[#0f0c18] border-t sm:border border-purple-500/30 sm:rounded-xl flex flex-col shadow-2xl p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-purple-300">Share to community</p>
            <p className="text-sm text-white font-semibold truncate">
              {trackTitle || beatTitle || reportTitle || liveShare?.title || "Post"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none px-2"
            aria-label="Close share dialog"
            data-testid="share-dialog-close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading destinations…</p>
          ) : totalDestinations === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">You're not in any crews or groups yet, and you don't own a club.</p>
              <p className="text-gray-600 text-xs mt-1">Join a crew or start a club from the Community hub.</p>
            </div>
          ) : (
            <>
              {destinations.crews?.length > 0 && (
                <section data-testid="share-section-crews">
                  <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-2">🤝 Crews</p>
                  <div className="space-y-2">
                    {destinations.crews.map((c) => renderRow(c, "crew"))}
                  </div>
                </section>
              )}
              {destinations.groups?.length > 0 && (
                <section data-testid="share-section-groups">
                  <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-2">👥 Groups</p>
                  <div className="space-y-2">
                    {destinations.groups.map((g) => renderRow(g, "group"))}
                  </div>
                </section>
              )}
              {destinations.clubs?.length > 0 && (
                <section data-testid="share-section-clubs">
                  <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">🎟️ Clubs (exclusive)</p>
                  <div className="space-y-2">
                    {destinations.clubs.map((c) => renderRow(c, "club"))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {totalDestinations > 0 && (
          <div className="border-t border-gray-800 p-3 space-y-2">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={selected?.kind === "club"
                ? "Exclusive message to subscribers (optional)…"
                : "Say something about this track (optional)…"}
              rows={2}
              maxLength={500}
              className="bg-black/50 border-gray-700 text-white min-h-[48px]"
              data-testid="share-dialog-note"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                {selected ? `→ ${selected.name}` : "Pick a destination"}
              </span>
              <Button
                onClick={handleSubmit}
                disabled={!selected || posting}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                data-testid="share-dialog-submit"
              >
                {posting ? "Sharing…" : "Share"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ShareToCommunityDialog;
